import { useCallback } from 'react';

import { useSelector } from '@xstate/react';

import { useGlobalAppState } from '../../../GlobalAppState/GlobalAppState';
import { type AzureAuthStateMachineEvents } from '../../machine/azure-auth-machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A simple hook for interacting with the Azure authentication machine using friendly abstractions.
 */
export const useAzureAuth = () => {
  const { azureAuthMachineRef } = useGlobalAppState();

  // ─── Flags ───────────────────────────────────────────────────────────

  const { isFillAccountDataStep, isCreatingNewAccount, isAuthenticationFailed, shouldRedirect } =
    useSelector(azureAuthMachineRef, (state) => {
      return {
        isFillAccountDataStep:
          state.matches('filling-account-data') ||
          state.matches('refreshing-access-tokens') ||
          state.matches('creating-account'),
        isCreatingNewAccount:
          state.matches('refreshing-access-tokens') || state.matches('creating-account'),
        isAuthenticationFailed:
          state.matches('authentication-failed') || state.matches('ad-authentication-failed'),
        shouldRedirect: state.matches('redirect'),
      };
    });

  const context = useSelector(azureAuthMachineRef, (state) => state.context);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const submitAccountData = useCallback(
    (input: SubmitAccountDataInput) => {
      azureAuthMachineRef.send({
        type: 'SUBMIT_ACCOUNT_DATA',
        input: {
          firstName: input.firstName,
          lastName: input.lastName,
          phoneNumber: input.phoneNumber,
          zipCode: input.zipCode,
          birthday: input.birthday,
        },
      });
    },
    [azureAuthMachineRef],
  );

  const initializeAuthenticationFlow = useCallback(() => {
    azureAuthMachineRef.send({ type: 'INITIALIZE' });
  }, [azureAuthMachineRef]);

  const resetAuthenticationFlow = useCallback(() => {
    azureAuthMachineRef.send({ type: 'RESET' });
  }, [azureAuthMachineRef]);

  const restartAuthenticationFlow = useCallback(() => {
    azureAuthMachineRef.send({ type: 'RESTART' });
    azureAuthMachineRef.send({ type: 'INITIALIZE' });
  }, [azureAuthMachineRef]);

  const completeAuthenticationFlow = useCallback(() => {
    azureAuthMachineRef.send({ type: 'COMPLETE' });
  }, [azureAuthMachineRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    context,
    state: {
      isFillAccountDataStep,
      isCreatingNewAccount,
      isAuthenticationFailed,
      shouldRedirect,
    },
    initializeAuthenticationFlow,
    resetAuthenticationFlow,
    restartAuthenticationFlow,
    completeAuthenticationFlow,
    submitAccountData,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SubmitAccountDataInput = Extract<
  AzureAuthStateMachineEvents,
  { type: 'SUBMIT_ACCOUNT_DATA' }
>['input'];
